import React from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import styled from 'styled-components';
import { withRouter, Redirect } from 'react-router-dom';
import { GlobalHotKeys } from "react-hotkeys";
import _ from 'lodash';
import { AnimatePresence, motion } from "framer-motion";
import { createBrowserHistory } from 'history';
import { isMobile } from 'react-device-detect'

import { PageScreen } from './PageScreen';
import { ModuleScreen } from './ModuleScreen';
import { AccessModule } from 'modules';
import { Spinner, Animation, WindowResizer, RichText } from 'components';
import {Toolbar, Side, Search, TaskQueue, ItemInputModal, Support, UpdatesModal, ItemModal } from 'features';
import { sideContext, pageContext, moduleContext, accessContext } from 'context';
import { Page } from 'schema';

import * as actionCreators from 'store/actions';
import classes from './MainScreen.module.css';
import * as cssTheme from 'theme'

const Container = styled.div`
    height: auto;
    width: calc(100vw - 0.5rem);
    display: ${props => props.side ? 'grid': 'flex'};
    grid-template-columns: ${props => props.side ? '15rem 1fr' : '1fr'};
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    flex-grow: 4;
    padding-top: 2.5rem;
`

const MainBody = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: ${props => props.orientation === 'horizontal' && '1fr auto'};
    grid-template-rows: ${props => props.orientation === 'vertical' && 'auto 1fr'};
`

const SpinContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
`

const AccessPlaceholder = styled.div`
    width: ${props => props.width || '30rem'};
    height: 100%;
    /* border: 1px solid red; */
    margin: 0 1rem;
`

const keyMap = {
    UNDO: { 
        name: 'Undo last action', 
        sequences: ['ctrl+z', { sequence: 'meta+z', action: 'keydown' }], 
        action: 'keydown' 
    },
    REDO: { 
        name: 'Redo last action', 
        sequences: ['ctrl+shift+z', { sequence: 'meta+shift+z', action: 'keydown' }], 
        action: 'keydown' 
    },
    CLOSE: {
        name: 'Close',
        sequence: 'esc',
        action: 'keydown'
    },
    SEARCH: {
        name: 'Search',
        sequences: ['ctrl+p', { sequence: 'meta+p', action: 'keydown' }],
        action: 'keydown'
    },
    ACCESS: {
        name: 'Search',
        sequences: ['ctrl+b', { sequence: 'meta+b', action: 'keydown' }],
        action: 'keydown'
    },
    MENU: {
        name: 'Menu',
        sequences: ['ctrl+m', { sequence: 'meta+m', action: 'keydown' }],
        action: 'keydown'
    },
    TOGGLE_VIEW: {
        name: 'Toggle view',
        sequences: ['ctrl+\\', { sequence: 'meta+\\', action: 'keydown' }],
        action: 'keydown'
    },
    SHORTCUTS: {
        name: 'Shortcuts',
        sequences: ['shift+?', { sequence: 'shift+?', action: 'keydown' }],
        action: 'keydown'
    },
    DIVIDER : {
        name: 'Divider',
        sequences: ['ctrl+d', { sequence: 'meta+d', action: 'keydown' }],
        action: 'keydown'
    },
    WIDTH : {
        name: 'Width',
        sequences: ['ctrl+shift+\\', { sequence: 'meta+shift+\\', action: 'keydown' }],
        action: 'keydown'
    },
    NEW_ITEM : {
        name: 'Create item',
        sequences: ['\\', { sequence: '\\', action: 'keydown' }],
        action: 'keydown'
    },
}

const MainScreen = props => {
    const dispatch = useDispatch()
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector(state => state.auth.user)
    const modalID = useSelector(state => state.content.modalID)
    const deferredLoaded = useSelector(state => state.db.present.deferredLoaded)
    const deferred = useSelector(state => state.db.present.deferred)
    // const inputID = ''
    // const inputID2 = ''
    const inputID = useSelector(state => state.content.inputID)
    const inputID2 = useSelector(state => state.content.inputID2)
    const sessionId = useSelector(state => state.content.sessionId)
    const access = useSelector(state => state.content.access)
    const showAccess = useSelector(state => state.content.showAccess)
    const accessProps = useSelector(state => state.content.accessProps)
    const loadedPages = useSelector(state => state.db.present.loadedPages)
    const activePageID = user && user['activePageID']
    const activePageType = user && user['activePageType']

    const side = useSelector(state => state.content.side)
    const body = useSelector(state => state.content.body)
    const bodyWidth = body && body.clientWidth
    const modalElement = useSelector(state => state.content.modalElement)
    const secModalElement = useSelector(state => state.content.secModalElement)

    const history = useSelector(state => state.db)
    const canUndo = useSelector(state => state.db.past.length > 0)
    const canRedo = useSelector(state => state.db.future.length > 0)
    // if(history.past.length > 0){
    //     const t = _.last(history.past).data.toJS()['pages']["6219f2a36ae8d2784a4135eb"]
    //     t && console.log(t['sections'], history.past)
    // }

    React.useEffect(() => {
        if(user && data && !deferredLoaded) {
            console.log({deferred})
            const pageID = user.show_completed && user['activePageType'] === 'Pages' && user['activePageID']
            dispatch(actionCreators.loadDeferred({
                data, 
                pageID: pageID,
                deferred,
                loadCompleted: process.env.REACT_APP_DISABLE_COMPLETED ? false : pageID ? true : false,
            }))
        }

    }, [user])

    //////////////
    // HANDLERS //
    //////////////
    const getPage = () => {
        let page_details = data['pages'][activePageID]
        page_details['pageID'] = activePageID
        const page = new Page(page_details)
        return page
    }

    const onHistory = (type) => {
        console.log(history)
        if(canUndo && type === 'undo') {
            const past = _.last(history.past).data.toJS()
            dispatch(actionCreators.timeTravel(type, past, history.present.data.toJS(), user['uid']))
        } else if (canRedo && type === 'redo') {
            const future = _.head(history.future).data.toJS()
            dispatch(actionCreators.timeTravel(type, future, history.present.data.toJS(), user['uid']))
        }
    }

    const onToggleView = (value) => {
        const updatedPage = getPage()
        updatedPage['page_view'] = value
        dispatch(actionCreators.updatePage(updatedPage, data, user['uid'], sessionId))
    }

    const keyHandlers = {
        UNDO: () => onHistory('undo'),
        REDO: () => onHistory('redo'),
        CLOSE: () => {
            modalID && dispatch(actionCreators.setModal(null,null))
            inputID && dispatch(actionCreators.setInput(inputID2 ? inputID : null, null))
        },
        SEARCH: (event) => {
            event.preventDefault()
            dispatch(actionCreators.setModal(<Search/>, 'SEARCH'))
        },
        ACCESS: (event) => {
            event.preventDefault()
            user['activePageType'] === pageContext.TYPES.PAGES && dispatch(
                actionCreators.setAccess(!showAccess, access || moduleContext.MODULES.CALENDAR, accessProps)
            )
        },
        MENU: (event) => {
            event.preventDefault()
            dispatch(actionCreators.setSide(side === sideContext.TYPES.OPENED ? sideContext.TYPES.CLOSED : sideContext.TYPES.OPENED))
        },
        TOGGLE_VIEW: (event) => {
            event.preventDefault()
            if(user['activePageType'] !== pageContext.TYPES.PAGES) { return }
            const page = getPage()
            const currentView = page['page_view']
            const updatedView = currentView === pageContext.VIEWS.LIST_VIEW ? pageContext.VIEWS.KANBAN_VIEW : pageContext.VIEWS.LIST_VIEW
            onToggleView(updatedView)
        },
        SHORTCUTS: (event) => {
            event.preventDefault()
            if(modalID === 'supportModal'){
                dispatch(actionCreators.setModal(null,null))
            } else {
                dispatch(actionCreators.setModal(null, 'supportModal'))
            }
        },
        DIVIDER: (event) => {
            event.preventDefault()
            if(user['activePageType'] !== pageContext.TYPES.PAGES) { return }
            let updatedUser = {...user}
            updatedUser['item_divider'] = !updatedUser['item_divider']
            dispatch(actionCreators.updateUser(updatedUser))
        },
        WIDTH: (event) => {
            event.preventDefault()
            if(user['activePageType'] !== pageContext.TYPES.PAGES) { return }
            let updatedUser = {...user}
            updatedUser['full_width'] = !updatedUser['full_width']
            dispatch(actionCreators.updateUser(updatedUser))
        },
        NEW_ITEM: (event) => {
            event.preventDefault()
            if(user['activePageType'] !== pageContext.TYPES.PAGES) { return }
            if(modalID === 'itemInputModal'){
                dispatch(actionCreators.setModal(null,null))
            } else {
                dispatch(actionCreators.setModal(<ItemInputModal/>, 'itemInputModal'))
            }
        },
    }

    /////////////
    // CONTENT //
    /////////////
    const redirect = localStorage.getItem('redirect')
    let content = <SpinContainer><Spinner text='Loading'/></SpinContainer>
    if(redirect === 'settings-subscription'){
        content = <ModuleScreen type='SETTINGS' section='subscription'/>
        if(user['activePageID'] !== 'SETTINGS'){
            let updatedUser = {...user}
            updatedUser['activePageID'] = 'SETTINGS-SUBSCRIPTION'
            updatedUser['activePageType'] = 'Modules'
            dispatch(actionCreators.updateUser(updatedUser))
            localStorage.removeItem('redirect')
        }
    } else if (user) {
        switch(user['activePageType']){
            case pageContext.TYPES.PAGES:
                content = <PageScreen/>
                break
            case pageContext.TYPES.MODULES:
                content = <ModuleScreen type={activePageID}/>
                break
            default:
                content = <div>Page not found</div>
        }
    }

    if(!user){
        return (<Redirect to='/loading'/>)
    } else {
        return (
            <WindowResizer>
                <GlobalHotKeys keyMap={keyMap} handlers={keyHandlers} allowChanges>
                    <div className={classes.MainScreen}>
                        <Toolbar/>
                        <Container side={side === sideContext.TYPES.OPENED}>
        
                            {/* Sidebar */}
                            <AnimatePresence key='sidebars' initial={false}>
                                {side === sideContext.TYPES.OPENED && 
                                <Animation key='sidebar' animation={'openSide'}>
                                    <Side type={sideContext.TYPES.OPENED}/>
                                </Animation>}
                            </AnimatePresence>
        
                            {/* Main body */}
                            <MainBody orientation={user['activePageType'] === pageContext.TYPES.PAGES ? 'horizontal'
                                                : user['activePageType'] !== pageContext.TYPES.PAGES & bodyWidth < 1000 ? 'vertical'
                                                : 'horizontal'}>
                                
                                {/* Main content */}
                                <div style={{flexGrow: 1, overflow: 'auto'}} draggable={false}>{content}</div>
        
                                {/* Access placeeholder */}
                                <AnimatePresence>
                                    {bodyWidth > 1000 & showAccess & activePageType !== pageContext.TYPES.MODULES ?
                                    <motion.div key='notifications' size='100%' position="relative" 
                                        transition={{ duration: 0.3 }} initial="closed" animate="open" exit="closed"
                                        variants={{
                                            closed: { x: 0, width: 0, height: '100%' },
                                            open:  { x: 0, width: 'auto', height: '100%' },
                                        }}>
                                        <AccessPlaceholder width={
                                            access !== 'CALENDAR' ? moduleContext.WIDTHS[access.toUpperCase()] 
                                            : access === 'CALENDAR' ? moduleContext.WIDTHS['CALENDAR'][accessProps['range']] 
                                            : '30rem'}/>
                                    </motion.div>
                                    : null}
                                </AnimatePresence>
        
                            </MainBody>
                        </Container>
                        
                        {/* Misc elements */}
                        {modalElement}
                        {secModalElement}
                        <TaskQueue/>
                        {!isMobile && <Support/>}
                        
                    </div>
                </GlobalHotKeys>
                
            </WindowResizer>
        )
    }
    
}

export default MainScreen